import { computed } from 'vue'
import type { AuthPort } from '@/shared/api/auth-port'
import { useAuthStore } from './store/auth'

export type { User } from './store/auth'

/**
 * Binding auth Pinia → shared transport (refresh/logout). Composition root
 * (`app/providers/configureAuthTransport`) вызывает это вместо прямого
 * импорта `auth/store`.
 */
export function createAuthTransportPort(): AuthPort {
  return {
    getToken: () => useAuthStore().token,
    setToken: (token) => useAuthStore().setToken(token),
    onRefreshFailed: () => useAuthStore().logout(),
  }
}

export function useAuthSession() {
  const authStore = useAuthStore()

  return {
    isAuthenticated: computed(() => authStore.isAuthenticated),
    isBuyer: computed(() => authStore.isBuyer),
    isSupplier: computed(() => authStore.isSupplier),
    isAdmin: computed(() => authStore.isAdmin),
    user: computed(() => authStore.user),
    company: computed(() => authStore.company),
    token: computed(() => authStore.token),
    hasRole: (role: string) => authStore.hasRole(role),
    logout: () => authStore.logout(),
    signOut: () => authStore.signOut(),
    fetchProfile: () => authStore.fetchProfile(),
    login: (credentials: { email: string; password: string }) => authStore.login(credentials),
    verifyTwoFactor: (payload: { challengeToken: string; code: string }) =>
      authStore.verifyTwoFactor(payload),
    register: (payload: Parameters<ReturnType<typeof useAuthStore>['register']>[0]) =>
      authStore.register(payload),
    resendEmailVerification: (email: string) => authStore.resendEmailVerification(email),
    requestPasswordReset: (email: string) => authStore.requestPasswordReset(email),
    resetPassword: (token: string, password: string) => authStore.resetPassword(token, password),
  }
}

/**
 * Публичный контракт для Profile-модуля: identity (user/company) - источник в Auth,
 * но операции обновления профиля/компании/пароля исторически живут в auth store,
 * т.к. работают с той же самой account-сущностью, что и текущая сессия.
 */
export function useAuthProfile() {
  const authStore = useAuthStore()

  return {
    user: computed(() => authStore.user),
    company: computed(() => authStore.company),
    fetchProfile: () => authStore.fetchProfile(),
    updateProfile: (data: Parameters<ReturnType<typeof useAuthStore>['updateProfile']>[0]) =>
      authStore.updateProfile(data),
    updateCompany: (data: Parameters<ReturnType<typeof useAuthStore>['updateCompany']>[0]) =>
      authStore.updateCompany(data),
    createCompany: (data: Parameters<ReturnType<typeof useAuthStore>['createCompany']>[0]) =>
      authStore.createCompany(data),
    changePassword: (data: Parameters<ReturnType<typeof useAuthStore>['changePassword']>[0]) =>
      authStore.changePassword(data),
    toggleTwoFactor: (enabled: boolean) => authStore.toggleTwoFactor(enabled),
  }
}

export async function ensureAuthSession(options: {
  requiresGuest?: boolean
  requiresSupplierGuest?: boolean
}): Promise<ReturnType<typeof useAuthStore>> {
  const authStore = useAuthStore()
  const shouldRestoreSession =
    !authStore.token && !options.requiresGuest && !options.requiresSupplierGuest

  if (!authStore.initialized && shouldRestoreSession) {
    await authStore.initAuth()
  }

  if (authStore.token && !authStore.user) {
    try {
      await authStore.fetchProfile()
    } catch {
      authStore.logout()
    }
  }

  return authStore
}

export function getAuthRouteAccess(authStore: ReturnType<typeof useAuthStore>) {
  return {
    isAuthenticated: authStore.isAuthenticated,
    isAdmin: authStore.isAdmin,
    isSupplier: authStore.isSupplier,
    logout: () => authStore.logout(),
  }
}
